import type { IUser } from 'core'

type JsonList = Array<JsonValue>

type JsonValue = boolean | number | string | null | JsonList | IProperties

export interface IProperties {
  [key: string]: JsonValue
  [index: number]: JsonValue
}

export interface IOneSignal {
  init(appId: string): Promise<void>
  getUserId(): Promise<string | null>
}

export interface IGoogleAnalytics {
  init(measurementId: string): Promise<void>
  identify(userId: string): Promise<void>
  track(eventName: string, properties?: IProperties): Promise<void>
  trackScreen(): Promise<void>
}

export interface ILogRocket<InitOptions = any> {
  init(appId: string, options?: InitOptions): Promise<void>
  identify(userId: string): Promise<void>
  track(eventName: string): Promise<void>
  trackScreen(): Promise<void>
}

export interface IMixpanel {
  init(token: string): Promise<void>
  identify(userId: IUser['id']): void
  setUserProperty(property: string, value: any): void
  setUserPropertyOnce(property: string, value: any): void
  track(eventName: string, properties?: IProperties): void
  reset(): void
}

export interface ISentry<Sentry> {
  init(): Sentry
  identify(userId: IUser['id']): void
  reset(): void
}

export interface AnalyticsProviders<LogRocket, Sentry> {
  googleAnalytics: IGoogleAnalytics
  sentry: ISentry<Sentry>
  initVitals(
    callback: (metric: {
      name: 'CLS' | 'FCP' | 'FID' | 'INP' | 'LCP' | 'TTFB'
      value: number
      delta: number
      id: string
    }) => void
  ): void
  logRocket: LogRocket
  mixpanel: IMixpanel
  oneSignal: IOneSignal
}

const emptyFn = () => {}
const emptyAsyncFn = async () => {}

// fake interfaces used by native and web when we don't want to connect to those services

export function getFakeProviders<ISentry>(Sentry: ISentry): AnalyticsProviders<ILogRocket, ISentry> {
  return {
    googleAnalytics: {
      init: emptyAsyncFn,
      identify: emptyAsyncFn,
      track: emptyAsyncFn,
      trackScreen: emptyAsyncFn
    },
    initVitals: emptyFn,
    logRocket: {
      init: emptyAsyncFn,
      identify: emptyAsyncFn,
      track: emptyAsyncFn,
      trackScreen: emptyAsyncFn
    },
    mixpanel: {
      init: emptyAsyncFn,
      identify: emptyFn,
      setUserProperty: emptyFn,
      setUserPropertyOnce: emptyFn,
      track: emptyFn,
      reset: emptyFn
    },
    oneSignal: {
      init: emptyAsyncFn,
      getUserId: async () => null
    },
    sentry: {
      init: () => Sentry,
      identify: emptyFn,
      reset: emptyFn
    }
  }
}
